import { Role } from "@/types/db";

export const roleLabel = (r: Role): string => ({
  employee: "Employee",
  manager: "Manager",
  admin: "Admin",
  c_suite: "C-Suite",
  accountant: "Accountant",
  super_admin: "Super Admin",
  kiosk: "Kiosk (warehouse)",
  investor: "Investor",
  sales_rep: "Sales Rep",
}[r]);

export const isAdmin = (r?: Role | null) => r === "admin" || r === "super_admin";
export const isAccountant = (r?: Role | null) => r === "accountant";
export const isInvestor = (r?: Role | null) => r === "investor";
export const isLeadership = (r?: Role | null) => r === "admin" || r === "super_admin" || r === "c_suite";

export function fmtMoney(n: number | null | undefined): string {
  if (n == null) return "—";
  return "$" + Number(n).toFixed(2);
}

export function fmtHours(h: number | null | undefined): string {
  if (h == null) return "—";
  return Number(h).toFixed(2) + " hr";
}

export function fmtDate(s: string | null | undefined): string {
  if (!s) return "—";
  return new Date(s).toLocaleString();
}

/**
 * A date with no time in it — "2026-09-18" — names a square on a calendar. It
 * is not an instant, and it must not be turned into one.
 *
 * DATE-002. `new Date("2026-09-18")` is specified to parse as midnight UTC, so
 * in America/Chicago it becomes 6pm on the 17th and toLocaleDateString() prints
 * Sep 17. A person who booked one day off saw the day before; goals stored as
 * 2026-09-10 to 2026-12-09 rendered as Sep 9 to Dec 8; Today's Checklists said
 * Sep 9 on the morning of the 10th. The stored dates were right the whole time.
 *
 * Date-only strings are therefore read as year/month/day components and built
 * in local time, where they name the same square everywhere. Anything carrying
 * a time — a real timestamp — keeps the old behaviour, because for an instant
 * the conversion to local time is the correct thing to do.
 */
const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

export function isDateOnly(s: string): boolean {
  return DATE_ONLY.test(s.trim());
}

export function fmtDay(s: string | null | undefined): string {
  if (!s) return "—";
  const m = DATE_ONLY.exec(s.trim());
  if (m) {
    // Months are 0-indexed; the day is constructed in local time on purpose.
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).toLocaleDateString();
  }
  return new Date(s).toLocaleDateString();
}

/** The same square, said shorter: "Sep 18". */
export function fmtDayShort(s: string | null | undefined): string {
  if (!s) return "—";
  const m = DATE_ONLY.exec(s.trim());
  const d = m
    ? new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
    : new Date(s);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/**
 * Today, as the calendar square the user is standing on — not as UTC sees it.
 *
 * DATE-001. `new Date().toISOString().slice(0, 10)` rolls over at UTC midnight,
 * which is 7pm in Chicago: the evening ritual on Sep 9 saved under Sep 10 and
 * the history then disagreed with the dashboard that had just shown Sep 9.
 */
export function todayISO(d: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

export function fmtTime(s: string | null | undefined): string {
  if (!s) return "—";
  return new Date(s).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

export function initials(first: string | null, last: string | null, email?: string): string {
  const f = (first || "").trim()[0];
  const l = (last || "").trim()[0];
  if (f || l) return ((f || "") + (l || "")).toUpperCase();
  if (email) return email.slice(0, 2).toUpperCase();
  return "—";
}

export function startOfWeek(d: Date = new Date()): Date {
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday
  return new Date(d.setDate(diff));
}

export function exportToCsv(filename: string, headers: string[], rows: (string | number | null | undefined)[][]): void {
  const escape = (v: string | number | null | undefined) =>
    `"${String(v ?? "").replace(/"/g, '""')}"`;
  const csv = [headers.map(escape).join(",")].concat(rows.map(r => r.map(escape).join(","))).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
}
